/**
 * Klaviyo Form Handler
 * Handles form submissions, Klaviyo profile sync, and event tracking for all forms.
 *
 * This script:
 * 1. Listens for form submissions
 * 2. Extracts form data and consent status
 * 3. Always identifies the profile and tracks "Form Submitted" event (for lead forms)
 * 4. If consent is given, also tracks "Marketing Consent Given" event
 * 5. The "Marketing Consent Given" event triggers a Klaviyo Flow that subscribes the profile
 *
 * Usage:
 *   <script src="{{ 'klaviyo-form-handler.js' | asset_url }}" defer></script>
 *   <script>
 *     document.addEventListener('DOMContentLoaded', function() {
 *       KlaviyoFormHandler.init({
 *         defaultListId: 'YOUR_LIST_ID',
 *         forms: [
 *           { selector: '#ContactForm', source: 'Contact Page' },
 *           { selector: '.newsletter-form', source: 'Newsletter Signup' },
 *           { selector: '#KlaviyoLeadForm', source: 'Lead Form', trackFormSubmitted: true }
 *         ]
 *       });
 *     });
 *   </script>
 */

(function () {
  'use strict';

  const KlaviyoFormHandler = {
    config: {
      consentCheckboxSelector: '[data-klaviyo-consent]',
      forms: [],
      debug: false,
      botProtection: {
        enabled: true,
        minSubmitTime: 3000, // Minimum time in ms before form can be submitted
        honeypotFieldName: 'contact[website]',
        timingFieldName: 'contact[form_loaded]',
      },
    },

    /**
     * Initialize the form handler
     * @param {Object} options - Configuration options
     */
    init: function (options) {
      // Merge forms array instead of replacing it
      // This allows multiple sections to register their forms
      if (options.forms && Array.isArray(options.forms)) {
        this.config.forms = this.config.forms.concat(options.forms);
        delete options.forms;
      }

      this.config = { ...this.config, ...options };

      if (this.config.debug) {
        console.log('[KlaviyoFormHandler] Initializing with config:', this.config);
      }

      // Initialize _learnq queue immediately
      window._learnq = window._learnq || [];

      // Attach form listeners synchronously to avoid race conditions
      // The _learnq queue will buffer events until Klaviyo SDK loads
      this.attachFormListeners();
    },

    /**
     * Wait for Klaviyo SDK to be available
     * Note: We use _learnq queue which works even before Klaviyo fully loads,
     * so we resolve immediately but still check for the SDK for debugging
     * @returns {Promise}
     */
    waitForKlaviyo: function () {
      return new Promise((resolve) => {
        // Initialize _learnq queue immediately - this is the recommended approach
        // The queue will be processed once Klaviyo SDK loads
        window._learnq = window._learnq || [];

        if (typeof window.klaviyo !== 'undefined') {
          if (this.config.debug) {
            console.log('[KlaviyoFormHandler] Klaviyo SDK already loaded');
          }
          resolve();
          return;
        }

        // Resolve immediately - _learnq queue works without waiting
        if (this.config.debug) {
          console.log('[KlaviyoFormHandler] Using _learnq queue (Klaviyo SDK will process when ready)');
        }
        resolve();
      });
    },

    /**
     * Attach listeners to configured forms
     */
    attachFormListeners: function () {
      this.config.forms.forEach((formConfig) => {
        const forms = document.querySelectorAll(formConfig.selector);
        forms.forEach((form) => {
          if (form.dataset.klaviyoHandlerAttached) return;

          form.addEventListener('submit', (e) => {
            this.handleFormSubmit(e, form, formConfig);
          });

          form.dataset.klaviyoHandlerAttached = 'true';

          if (this.config.debug) {
            console.log('[KlaviyoFormHandler] Attached to form:', formConfig.selector);
          }
        });
      });
    },

    /**
     * Handle form submission
     * @param {Event} e - Submit event
     * @param {HTMLFormElement} form - The form element
     * @param {Object} formConfig - Configuration for this form
     */
    handleFormSubmit: function (e, form, formConfig) {
      const formData = this.extractFormData(form);
      const hasConsent = this.checkConsent(form);
      const trackFormSubmitted = formConfig.trackFormSubmitted || false;

      if (this.config.debug) {
        console.log('[KlaviyoFormHandler] Form submitted:', {
          formData,
          hasConsent,
          trackFormSubmitted,
          source: formConfig.source,
        });
      }

      // Bot detection check
      const botCheck = this.isLikelyBot(form, formData);
      if (botCheck.isBot) {
        if (this.config.debug) {
          console.log('[KlaviyoFormHandler] Bot submission blocked:', botCheck.reason);
        }
        // Prevent form submission and stop other event listeners (e.g., Meta Pixel)
        e.preventDefault();
        e.stopImmediatePropagation();
        // Show error message to user (in case it's a false positive)
        this.showBotError(form);
        return;
      }

      // For lead forms: always identify profile and track "Form Submitted"
      // regardless of marketing consent
      if (trackFormSubmitted && formData.email) {
        this.identifyAndTrackFormSubmission(formData, formConfig.source, hasConsent);
      }
      // For other forms: only sync if consent is given
      else if (hasConsent && formData.email) {
        this.syncToKlaviyo(formData, formConfig.source);
      } else if (this.config.debug) {
        console.log('[KlaviyoFormHandler] Skipping Klaviyo sync - no consent or no email');
      }
    },

    /**
     * Check if marketing consent checkbox is checked
     * @param {HTMLFormElement} form
     * @returns {boolean}
     */
    checkConsent: function (form) {
      const checkbox = form.querySelector(this.config.consentCheckboxSelector);
      return checkbox ? checkbox.checked : false;
    },

    /**
     * Extract relevant data from form
     * @param {HTMLFormElement} form
     * @returns {Object}
     */
    extractFormData: function (form) {
      const data = {
        email: '',
        firstName: '',
        lastName: '',
        phone: '',
        fullName: '',
        customProperties: {},
      };

      // Get form data
      const formData = new FormData(form);

      // Email - check various field names, with DOM fallback
      data.email =
        formData.get('contact[email]') ||
        formData.get('email') ||
        formData.get('customer[email]') ||
        '';

      // DOM fallback for email
      if (!data.email) {
        const emailInput = form.querySelector('[name="contact[email]"], [name="email"], [type="email"]');
        if (emailInput) {
          data.email = emailInput.value.trim();
        }
      }

      // First name - try separate field first, then combined Name field
      let firstName =
        formData.get('contact[Vorname]') ||
        formData.get('contact[vorname]') ||
        formData.get('contact[FirstName]') ||
        formData.get('contact[first_name]') ||
        '';

      // DOM fallback for first name
      if (!firstName) {
        const firstNameInput = form.querySelector('[name="contact[Vorname]"], [name="contact[FirstName]"], [name="contact[first_name]"]');
        if (firstNameInput) {
          firstName = firstNameInput.value.trim();
        }
      }

      // Last name - try separate field
      let lastName =
        formData.get('contact[Nachname]') ||
        formData.get('contact[nachname]') ||
        formData.get('contact[LastName]') ||
        formData.get('contact[last_name]') ||
        '';

      // DOM fallback for last name
      if (!lastName) {
        const lastNameInput = form.querySelector('[name="contact[Nachname]"], [name="contact[LastName]"], [name="contact[last_name]"]');
        if (lastNameInput) {
          lastName = lastNameInput.value.trim();
        }
      }

      // Fallback: try combined Name field if separate fields are empty
      if (!firstName && !lastName) {
        let nameField =
          formData.get('contact[Name]') ||
          formData.get('contact[name]') ||
          formData.get('name') ||
          formData.get('customer[name]') ||
          '';

        // DOM fallback for combined name field
        if (!nameField) {
          const nameInput = form.querySelector('[name="contact[Name]"], [name="contact[name]"], [name="name"]');
          if (nameInput) {
            nameField = nameInput.value.trim();
          }
        }

        if (nameField) {
          const nameParts = nameField.trim().split(/\s+/);
          firstName = nameParts[0] || '';
          lastName = nameParts.slice(1).join(' ') || '';
        }
      }

      data.firstName = firstName;
      data.lastName = lastName;
      data.fullName = [firstName, lastName].filter(Boolean).join(' ');

      // Phone - try FormData first, then DOM fallback
      data.phone =
        formData.get('contact[Telefon]') ||
        formData.get('contact[phone]') ||
        formData.get('contact[Phone]') ||
        formData.get('phone') ||
        '';

      // DOM fallback for phone
      if (!data.phone) {
        const phoneInput = form.querySelector('[name="contact[Telefon]"], [name="contact[phone]"], [type="tel"]');
        if (phoneInput) {
          data.phone = phoneInput.value.trim();
        }
      }

      // Collect all other contact fields as custom properties
      for (const [key, value] of formData.entries()) {
        if (key.startsWith('contact[') && value) {
          const propName = key.replace('contact[', '').replace(']', '');
          // Skip standard fields we've already processed, and bot protection fields
          if (!['email', 'name', 'Name', 'Vorname', 'vorname', 'Nachname', 'nachname', 'FirstName', 'first_name', 'LastName', 'last_name', 'phone', 'Phone', 'Telefon', 'accepts_marketing', 'website', 'form_loaded'].includes(propName)) {
            data.customProperties[propName] = value;
          }
        }
      }

      return data;
    },

    /**
     * Identify profile and track form submission (for lead forms)
     * Always tracks "Form Submitted", optionally tracks "Marketing Consent Given"
     * Uses _learnq queue for reliable tracking even when page navigates
     * @param {Object} formData - Extracted form data
     * @param {string} source - Form source identifier
     * @param {boolean} hasConsent - Whether marketing consent was given
     */
    /**
     * Klaviyo's onsite JS exposes no reset/clear-identity API (only identify/track),
     * but identify() with a NEW email does not switch the browser's tracked identity:
     * client-side track() keeps hitting the profile already resolved via the __kla_id
     * cookie, so a second signup with a different email in the same browser gets its
     * events (Form Submitted, consent, flow trigger) attributed to the FIRST profile.
     * Fix: decode __kla_id, and if it carries a different email than the form, expire
     * the cookie before identifying, so the new identify resolves a fresh identity.
     * Anonymous cookies (no $email) are left alone so normal anonymous-to-known
     * merging keeps working.
     * @param {string} formEmail
     * @returns {boolean} true if a mismatched identity cookie was cleared
     */
    resetIdentityIfMismatched: function (formEmail) {
      if (!formEmail) return false;
      try {
        const match = document.cookie.match(/(?:^|;\s*)__kla_id=([^;]*)/);
        if (!match || !match[1]) return false;
        const decoded = JSON.parse(atob(decodeURIComponent(match[1])));
        const cookieEmail = decoded && decoded.$email;
        if (!cookieEmail || cookieEmail.toLowerCase() === formEmail.toLowerCase()) {
          return false;
        }
        // Expire on every plausible domain scope (Klaviyo sets it on the registrable domain)
        const expire = '__kla_id=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;';
        document.cookie = expire;
        const parts = window.location.hostname.split('.');
        for (let i = 0; i < parts.length - 1; i++) {
          document.cookie = expire + ' domain=.' + parts.slice(i).join('.') + ';';
        }
        if (this.config.debug) {
          console.log('[KlaviyoFormHandler] Cleared mismatched Klaviyo identity (was ' + cookieEmail + ', form uses ' + formEmail + ')');
        }
        return true;
      } catch (e) {
        if (this.config.debug) {
          console.error('[KlaviyoFormHandler] Identity mismatch check failed:', e);
        }
        return false;
      }
    },

    identifyAndTrackFormSubmission: function (formData, source, hasConsent) {
      const timestamp = new Date().toISOString();

      this.resetIdentityIfMismatched(formData.email);

      // Build profile data
      const profileData = {
        $email: formData.email,
        Last_Form_Source: source,
      };

      // Add marketing consent status and subscription
      if (hasConsent) {
        profileData['Accepts Marketing'] = true;
        // $consent is required to actually subscribe the user to email marketing
        // This tells Klaviyo the user has given explicit consent
        profileData.$consent = ['email'];
      }

      // Add name if available
      if (formData.firstName) {
        profileData.$first_name = formData.firstName;
      }
      if (formData.lastName) {
        profileData.$last_name = formData.lastName;
      }

      // Add phone if available (format for Klaviyo)
      if (formData.phone) {
        profileData.$phone_number = this.formatPhoneNumber(formData.phone);
      }

      // Add custom properties (excluding redundant ones)
      const excludeProps = ['Produkt-URL', 'URL', 'Page_URL', 'tags', 'Form_Source'];
      for (const [key, value] of Object.entries(formData.customProperties)) {
        if (!excludeProps.includes(key)) {
          profileData[key] = value;
        }
      }

      // Use multiple methods to ensure profile is identified reliably
      var _learnq = window._learnq || [];
      window._learnq = _learnq;

      // Method 1: Use Klaviyo's direct identify method if SDK is loaded
      if (typeof window.klaviyo !== 'undefined' && typeof window.klaviyo.identify === 'function') {
        try {
          window.klaviyo.identify(profileData);
          if (this.config.debug) {
            console.log('[KlaviyoFormHandler] Profile identified via klaviyo.identify():', profileData);
          }
        } catch (e) {
          if (this.config.debug) {
            console.error('[KlaviyoFormHandler] klaviyo.identify() failed:', e);
          }
        }
      }

      // Method 2: Also use _learnq queue as fallback/additional method
      _learnq.push(['identify', profileData]);

      if (this.config.debug) {
        console.log('[KlaviyoFormHandler] Profile identified via _learnq:', profileData);
      }

      // Always track "Form Submitted" event for lead forms
      const formSubmittedData = {
        Source: source,
        Form_Type: 'Lead Generation',
        Submission_Date: timestamp,
        Has_Marketing_Consent: hasConsent,
      };

      // Add name to event data for visibility
      if (formData.firstName) {
        formSubmittedData.First_Name = formData.firstName;
      }
      if (formData.lastName) {
        formSubmittedData.Last_Name = formData.lastName;
      }
      if (formData.fullName) {
        formSubmittedData.Full_Name = formData.fullName;
      }

      // Add custom properties to event
      for (const [key, value] of Object.entries(formData.customProperties)) {
        if (!excludeProps.includes(key)) {
          formSubmittedData[key] = value;
        }
      }

      // Build customer properties to pass with track call (updates profile)
      const customerProperties = {
        $email: formData.email,
      };
      if (formData.firstName) {
        customerProperties.$first_name = formData.firstName;
      }
      if (formData.lastName) {
        customerProperties.$last_name = formData.lastName;
      }
      if (formData.phone) {
        customerProperties.$phone_number = this.formatPhoneNumber(formData.phone);
      }

      // Method 1: Use Klaviyo's direct track method if SDK is loaded
      if (typeof window.klaviyo !== 'undefined' && typeof window.klaviyo.track === 'function') {
        try {
          window.klaviyo.track('Form Submitted', formSubmittedData, customerProperties);
          if (this.config.debug) {
            console.log('[KlaviyoFormHandler] Event tracked via klaviyo.track():', formSubmittedData, customerProperties);
          }
        } catch (e) {
          if (this.config.debug) {
            console.error('[KlaviyoFormHandler] klaviyo.track() failed:', e);
          }
        }
      }

      // Method 2: Also use _learnq queue with customer properties (4th argument)
      _learnq.push(['track', 'Form Submitted', formSubmittedData, customerProperties]);

      if (this.config.debug) {
        console.log('[KlaviyoFormHandler] Event tracked via _learnq: Form Submitted', formSubmittedData, customerProperties);
      }

      // If consent was given, also track "Marketing Consent Given"
      if (hasConsent) {
        const consentData = {
          Source: source,
          Consent_Date: timestamp,
        };

        _learnq.push(['track', 'Marketing Consent Given', consentData]);

        if (this.config.debug) {
          console.log('[KlaviyoFormHandler] Event tracked via _learnq: Marketing Consent Given', consentData);
        }
      }
    },

    /**
     * Sync profile to Klaviyo with consent (for non-lead forms)
     * Uses _learnq queue for reliable tracking even when page navigates
     * @param {Object} formData - Extracted form data
     * @param {string} source - Form source identifier
     */
    syncToKlaviyo: function (formData, source) {
      const timestamp = new Date().toISOString();

      this.resetIdentityIfMismatched(formData.email);

      // Build profile data - minimal, avoiding duplicates
      const profileData = {
        $email: formData.email,
        'Accepts Marketing': true,
        Last_Form_Source: source,
        // $consent is required to actually subscribe the user to email marketing
        // This tells Klaviyo the user has given explicit consent
        $consent: ['email'],
      };

      // Add name if available
      if (formData.firstName) {
        profileData.$first_name = formData.firstName;
      }
      if (formData.lastName) {
        profileData.$last_name = formData.lastName;
      }

      // Add phone if available (format for Klaviyo)
      if (formData.phone) {
        profileData.$phone_number = this.formatPhoneNumber(formData.phone);
      }

      // Add custom properties (excluding redundant ones)
      const excludeProps = ['Produkt-URL', 'URL', 'Page_URL'];
      for (const [key, value] of Object.entries(formData.customProperties)) {
        if (!excludeProps.includes(key)) {
          profileData[key] = value;
        }
      }

      // Use _learnq queue for reliable tracking (works even if page navigates)
      // This queue is processed by Klaviyo's SDK and persists across page loads
      var _learnq = window._learnq || [];
      window._learnq = _learnq;

      // Identify the profile with $consent to subscribe to email
      _learnq.push(['identify', profileData]);

      if (this.config.debug) {
        console.log('[KlaviyoFormHandler] Profile identified and subscribed via _learnq:', profileData);
      }

      // Track the consent event - this triggers the Klaviyo Flow
      const eventData = {
        Source: source,
        Consent_Date: timestamp,
      };

      _learnq.push(['track', 'Marketing Consent Given', eventData]);

      if (this.config.debug) {
        console.log('[KlaviyoFormHandler] Event tracked via _learnq: Marketing Consent Given', eventData);
      }
    },

    /**
     * Format phone number for Klaviyo (E.164 format preferred)
     * @param {string} phone
     * @returns {string}
     */
    formatPhoneNumber: function (phone) {
      // Remove all non-digit characters except leading +
      let cleaned = phone.replace(/[^\d+]/g, '');

      // If it doesn't start with +, assume German number
      if (!cleaned.startsWith('+')) {
        // Remove leading 0 if present and add +49
        if (cleaned.startsWith('0')) {
          cleaned = '+49' + cleaned.substring(1);
        } else if (cleaned.startsWith('49')) {
          cleaned = '+' + cleaned;
        } else {
          cleaned = '+49' + cleaned;
        }
      }

      return cleaned;
    },

    /**
     * Check if a single word looks like gibberish
     * @param {string} word
     * @returns {boolean}
     */
    isGibberishWord: function (word) {
      if (!word || word.length < 8) return false;

      // Check 1: Long letter-only word (12+ chars with no breaks)
      if (/^[a-zA-Z]{12,}$/.test(word)) {
        console.log('[KlaviyoFormHandler] Gibberish word: long letter-only:', word);
        return true;
      }

      // Check 2: High consonant-to-vowel ratio
      if (word.length >= 8 && /^[a-zA-Z]+$/.test(word)) {
        const vowels = (word.match(/[aeiouäöü]/gi) || []).length;
        const consonants = (word.match(/[bcdfghjklmnpqrstvwxyz]/gi) || []).length;

        if (vowels === 0 && consonants > 4) {
          console.log('[KlaviyoFormHandler] Gibberish word: no vowels:', word);
          return true;
        }

        const ratio = consonants / Math.max(vowels, 1);
        if (ratio > 3) {
          console.log('[KlaviyoFormHandler] Gibberish word: high consonant ratio (' + ratio.toFixed(1) + '):', word);
          return true;
        }
      }

      // Check 3: Random mixed case (e.g., "lePMMscYMx")
      if (word.length >= 8 && /^[a-zA-Z]+$/.test(word)) {
        const upperCount = (word.match(/[A-Z]/g) || []).length;
        const lowerCount = (word.match(/[a-z]/g) || []).length;

        // Random mixed case: has multiple upper AND lower, not normal capitalization
        if (upperCount > 2 && lowerCount > 2 && !/^[A-Z][a-z]+$/.test(word)) {
          console.log('[KlaviyoFormHandler] Gibberish word: random mixed case:', word);
          return true;
        }
      }

      // Check 4: Alternating case pattern (e.g., "aBcDeFgHi")
      if (/^(?:[a-z][A-Z]|[A-Z][a-z]){4,}/.test(word)) {
        console.log('[KlaviyoFormHandler] Gibberish word: alternating case:', word);
        return true;
      }

      return false;
    },

    /**
     * Check if a string looks like gibberish (random letters from bots)
     * Works for both single words (names) and multi-word strings (comments)
     * @param {string} text
     * @returns {boolean}
     */
    isGibberish: function (text) {
      if (!text || typeof text !== 'string') return false;

      const trimmed = text.trim();

      // Skip very short strings
      if (trimmed.length < 8) return false;

      // For single-word strings (like names), check directly
      if (!/\s/.test(trimmed)) {
        return this.isGibberishWord(trimmed);
      }

      // For multi-word strings (like comments), check each word
      // If any word is gibberish, the whole text is suspicious
      const words = trimmed.split(/\s+/);
      for (const word of words) {
        if (this.isGibberishWord(word)) {
          console.log('[KlaviyoFormHandler] Gibberish detected in multi-word text, bad word:', word);
          return true;
        }
      }

      // Also check the entire text for high consonant ratio (catches "qwrt zxcv dfgh")
      const vowels = (trimmed.match(/[aeiouäöü]/gi) || []).length;
      const consonants = (trimmed.match(/[bcdfghjklmnpqrstvwxyz]/gi) || []).length;
      const ratio = consonants / Math.max(vowels, 1);

      if (trimmed.length >= 15 && ratio > 3.5) {
        console.log('[KlaviyoFormHandler] Gibberish detected: overall high consonant ratio (' + ratio.toFixed(1) + '):', trimmed);
        return true;
      }

      return false;
    },

    /**
     * Check if submission is likely from a bot
     * @param {HTMLFormElement} form - The form element
     * @param {Object} formData - Extracted form data
     * @returns {Object} { isBot: boolean, reason: string }
     */
    isLikelyBot: function (form, formData) {
      console.log('[KlaviyoFormHandler] Bot check running with formData:', {
        firstName: formData.firstName,
        lastName: formData.lastName,
        fullName: formData.fullName,
        customProperties: formData.customProperties
      });

      if (!this.config.botProtection.enabled) {
        console.log('[KlaviyoFormHandler] Bot protection is disabled');
        return { isBot: false, reason: null };
      }

      // Check 1: Honeypot field filled (bots fill hidden fields)
      const honeypot = form.querySelector('[name="' + this.config.botProtection.honeypotFieldName + '"]');
      console.log('[KlaviyoFormHandler] Honeypot field:', honeypot ? honeypot.value : 'not found');
      if (honeypot && honeypot.value && honeypot.value.trim() !== '') {
        console.log('[KlaviyoFormHandler] Bot detected: honeypot filled with:', honeypot.value);
        return { isBot: true, reason: 'honeypot' };
      }

      // Check 2: Form submitted too quickly (bots submit instantly)
      const timingField = form.querySelector('[name="' + this.config.botProtection.timingFieldName + '"]');
      console.log('[KlaviyoFormHandler] Timing field:', timingField ? timingField.value : 'not found');
      if (timingField && timingField.value) {
        const loadTime = parseInt(timingField.value, 10);
        const submitTime = Date.now();
        const elapsedTime = submitTime - loadTime;
        console.log('[KlaviyoFormHandler] Elapsed time:', elapsedTime + 'ms');

        if (elapsedTime < this.config.botProtection.minSubmitTime) {
          console.log('[KlaviyoFormHandler] Bot detected: form submitted too fast (' + elapsedTime + 'ms)');
          return { isBot: true, reason: 'timing' };
        }
      }

      // Check 3: Gibberish in name fields
      if (this.isGibberish(formData.firstName)) {
        return { isBot: true, reason: 'gibberish_firstname' };
      }
      if (this.isGibberish(formData.lastName)) {
        return { isBot: true, reason: 'gibberish_lastname' };
      }
      if (this.isGibberish(formData.fullName)) {
        return { isBot: true, reason: 'gibberish_fullname' };
      }

      // Check 4: Gibberish in comment/message field
      const commentField = formData.customProperties['Kommentar'] ||
                          formData.customProperties['Nachricht'] ||
                          formData.customProperties['Comment'] ||
                          formData.customProperties['comment'] ||
                          formData.customProperties['Message'] ||
                          formData.customProperties['message'] ||
                          '';
      console.log('[KlaviyoFormHandler] Comment field value:', commentField);
      console.log('[KlaviyoFormHandler] Comment field length:', commentField.length);
      if (this.isGibberish(commentField)) {
        console.log('[KlaviyoFormHandler] Bot detected: gibberish in comment');
        return { isBot: true, reason: 'gibberish_comment' };
      }

      return { isBot: false, reason: null };
    },

    /**
     * Show bot detection error on form
     * @param {HTMLFormElement} form - The form element
     */
    showBotError: function (form) {
      // Try to find the error element for this form
      const formId = form.id || 'ContactForm';
      const errorElement = document.getElementById(formId + '-bot-error');
      if (errorElement) {
        errorElement.style.display = 'block';
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },

    /**
     * Manually sync a profile (can be called externally)
     * @param {Object} data - Profile data
     * @param {string} source - Source identifier
     */
    syncProfile: function (data, source) {
      const formData = {
        email: data.email || '',
        firstName: data.firstName || data.first_name || '',
        lastName: data.lastName || data.last_name || '',
        phone: data.phone || '',
        fullName: data.name || `${data.firstName || ''} ${data.lastName || ''}`.trim(),
        customProperties: data.customProperties || {},
      };

      this.syncToKlaviyo(formData, source);
    },
  };

  // Expose globally
  window.KlaviyoFormHandler = KlaviyoFormHandler;
})();
